import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { Chip } from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import filesize from 'filesize';
import clsx from 'clsx';

import { truncate } from '../../utils/truncate';
import Pdf from '../svg/Pdf';
import ImageFile from '../svg/ImageFile';
import ErrorIcon from '../svg/ErrorIcon';

const useStyles = makeStyles((theme) => ({
  chip: {
    margin: theme.spacing(0.5, 1, 0.5, 0),
    borderRadius: '4px !important',
    border: `2px solid ${theme.palette.grey['300']}`,
    backgroundColor: theme.palette.primary.contrastText,
    height: 'auto !important',
    padding: theme.spacing(1.5),
    '& .MuiChip-outlined:hover': {
      border: `1px solid ${theme.palette.primary.main}`,
    },
    '& .MuiChip-icon': {
      color: theme.palette.grey['300'],
    },
    '& .MuiChip-label': {
      fontSize: 12,
    },
    '& .MuiChip-deleteIcon': {
      marginLeft: theme.spacing(0.5),
      marginRight: '0 !important',
    },
    '$ .MuiChip-clickable': {
      backgroundColor: theme.palette.common.white,
    },
  },
  chipError: {
    border: `2px solid ${theme.palette.error.light}`,
    backgroundColor: theme.palette.error.lighter,
    '$ .MuiChip-clickable': {
      backgroundColor: theme.palette.error.lighter,
    },
  },
  fileType: {
    display: 'flex',
  },
  fileName: {
    display: 'flex',
    fontWeight: 500,
  },
  fileSubText: {
    fontSize: 11,
    color: theme.palette.grey.dark,
  },
  alertIcon: {
    alignSelf: 'center !important',
    height: '15px !important',
    width: '15px !important',
    marginRight: theme.spacing(1),
  },
  fileIcon: {
    height: '30px !important',
    width: '22px !important',
  },
}));

const FileThumbnail = (props) => {
  const { file, error, onDelete } = props;
  const classes = useStyles();
  const theme = useTheme();

  const renderFileType = useCallback(
    (type) => {
      switch (type) {
        case 'application/pdf':
          return <Pdf viewBox="0 0 22 25" className={classes.fileIcon} />;
        default:
          return <ImageFile viewBox="0 0 22 25" className={classes.fileIcon} />;
      }
    },
    [file.type],
  );

  return (
    <Chip
      icon={renderFileType(file.type)}
      label={
        <div>
          <div className={classes.fileName}>
            {error && (
              <ErrorIcon
                className={classes.alertIcon}
                htmlColor={theme.palette.error.main}
                viewBox="0 0 21 20"
              />
            )}
            {truncate(file.name, 20)}
          </div>
          <div className={classes.fileSubText}>{filesize(file.size, { spacer: '' })}</div>
        </div>
      }
      onDelete={onDelete}
      variant="outlined"
      className={clsx(classes.chip, { [classes.chipError]: !!error })}
      title={error || file.name}
      deleteIcon={<CloseIcon />}
      clickable={false}
      data-testid={file.name}
    />
  );
};

FileThumbnail.propTypes = {
  file: PropTypes.shape({
    type: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    size: PropTypes.number.isRequired,
  }).isRequired,
  error: PropTypes.string.isRequired,
  onDelete: PropTypes.func.isRequired,
};

export default FileThumbnail;
